/// <reference types="google.maps" />
import { Injectable, InjectionToken, inject } from '@angular/core';

import { AppConfigService } from 'src/app/core/services/app-config.service';
import { environment } from 'src/environments/environment';
import { SuggestedAddress, addressFromPlace } from './address-from-place';
import { MapsWindow, createPlacesLoader } from './google-maps-loader';

/**
 * The browser key, from this build's environment, and the countries, from the
 * backend's /public-config. No key or no country means no suggestions.
 */
export interface PlacesConfig {
  apiKey: string;
  countries: readonly string[];
}

export const PLACES_CONFIG = new InjectionToken<PlacesConfig>('PLACES_CONFIG', {
  providedIn: 'root',
  factory: () => ({
    apiKey: environment.googleMapsApiKey ?? '',
    countries: inject(AppConfigService).placesCountries(),
  }),
});

/** Makes the once-only loader of Google's Places library; replaced in tests. */
export type PlacesLoaderFactory = (apiKey: string, onRefused: () => void) => () => Promise<google.maps.PlacesLibrary>;

export const PLACES_LOADER = new InjectionToken<PlacesLoaderFactory>('PLACES_LOADER', {
  providedIn: 'root',
  factory: () => (apiKey, onRefused) => createPlacesLoader(window as MapsWindow, apiKey, onRefused),
});

/** A suggestion as the list shows it, with what is needed to fetch its address. */
export interface AddressSuggestion {
  main: string;
  secondary: string;
  prediction: google.maps.places.PlacePrediction;
}

/**
 * One search's billing session. Its suggestion requests and the address of the
 * suggestion picked share it, so Google bills them as one session.
 */
export type PlacesSession = google.maps.places.AutocompleteSessionToken;

/** Addresses, and streets for when the number is not typed yet. */
const ADDRESS_TYPES = ['street_address', 'premise', 'subpremise', 'route'];

/**
 * Address suggestions from Google Places (New), through the Maps JavaScript API.
 *
 * Off — every street field a plain input — without a key or a configured
 * country, and for the rest of the session once Google refuses the key or the
 * day's quota is used up. Nothing typed and nothing suggested is ever logged.
 */
@Injectable({ providedIn: 'root' })
export class AddressAutocompleteService {
  /** The countries suggestions may come from. */
  readonly countries: readonly string[];

  private readonly config = inject(PLACES_CONFIG);
  private readonly load: () => Promise<google.maps.PlacesLibrary>;
  private refused = false;

  constructor() {
    this.countries = this.config.countries;
    this.load = inject(PLACES_LOADER)(this.config.apiKey.trim(), () => this.giveUp());
  }

  /** Whether suggestions can be offered at all. */
  get available(): boolean {
    return !!this.config.apiKey.trim() && this.countries.length > 0 && !this.refused;
  }

  /** Loads Google's script ahead of the first search — when a street field gets focus. */
  warmUp(): void {
    if (this.available) {
      this.load().catch(() => this.giveUp());
    }
  }

  /**
   * Suggestions for what was typed, from the given countries only. A search with
   * no session starts one; the session comes back to be used for the rest of it.
   */
  async suggest(
    input: string,
    regionCodes: readonly string[],
    session: PlacesSession | null,
  ): Promise<{ suggestions: AddressSuggestion[]; session: PlacesSession | null }> {
    if (!this.available || !regionCodes.length) {
      return { suggestions: [], session };
    }
    const places = await this.load().catch(() => null);
    if (!places) {
      this.giveUp();
      return { suggestions: [], session };
    }
    const token = session ?? new places.AutocompleteSessionToken();
    try {
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: token,
        includedRegionCodes: regionCodes.map((code) => code.toLowerCase()),
        includedPrimaryTypes: ADDRESS_TYPES,
      });
      return {
        suggestions: suggestions
          .map((suggestion) => suggestion.placePrediction)
          .filter((prediction): prediction is google.maps.places.PlacePrediction => !!prediction)
          .map((prediction) => ({
            prediction,
            main: prediction.mainText?.text ?? prediction.text.text,
            secondary: prediction.secondaryText?.text ?? '',
          })),
        session: token,
      };
    } catch (error) {
      this.noteFailure(error);
      return { suggestions: [], session: token };
    }
  }

  /**
   * The address of a picked suggestion: the one Place Details request of the
   * session, which closes it. Asks for the address components and nothing else.
   * Null when it cannot be had.
   */
  async addressOf(suggestion: AddressSuggestion): Promise<SuggestedAddress | null> {
    try {
      const { place } = await suggestion.prediction.toPlace().fetchFields({ fields: ['addressComponents'] });
      return addressFromPlace(place.addressComponents ?? []);
    } catch (error) {
      this.noteFailure(error);
      return null;
    }
  }

  private noteFailure(error: unknown): void {
    if (isRefusal(error)) {
      this.giveUp();
    }
  }

  private giveUp(): void {
    if (!this.refused) {
      this.refused = true;
      console.warn('[AddressSuggestions] Google cannot be used right now; address fields are plain inputs for this session');
    }
  }
}

/** Google refusing this key: a wrong key, a site it is not allowed on, or the day's quota used up. */
function isRefusal(error: unknown): boolean {
  const failure = error as { code?: unknown; status?: unknown; message?: unknown } | null;
  const text = `${failure?.code ?? ''} ${failure?.status ?? ''} ${failure?.message ?? ''}`.toUpperCase();
  return /REQUEST_DENIED|PERMISSION_DENIED|RESOURCE_EXHAUSTED|OVER_QUERY_LIMIT|API_KEY|QUOTA|\b403\b|\b429\b/.test(text);
}
